import { socketManager } from "./socketManager";
import { postWithJwt } from "./requests";
import { jobTracker } from "../stores/jobTracker";
import { alertStore } from "stores";
import { errorTracker } from "./errorTracker";

interface JobOptions<T> {
  endpoint: string;
  payload: any;
  onSuccess?: (result: T, jobId: string, job?: any) => void;
  onError?: (error: any) => void;
  onProgress?: (progress: number) => void;
  successMessage?: string;
  errorMessage?: string;
  timeout?: number; // in milliseconds
  showSpinner?: boolean; // whether to show the global job spinner
}

export async function executeJob<T>({
  endpoint,
  payload,
  onSuccess,
  onError,
  onProgress,
  successMessage,
  errorMessage = "An error occurred",
  timeout = 30000, // 30 seconds default
  showSpinner = true, // show spinner by default
}: JobOptions<T>): Promise<T> {

  return new Promise(async (resolve, reject) => {
    let timeoutId: NodeJS.Timeout;
    let jobId: string | undefined;

    try {
      // Create job
      const response = await postWithJwt(endpoint, payload);

      if (response.error && !response.data) {
        alertStore.show(errorMessage, "error");
        errorTracker.captureError(response.error as Error | string, "error", {
          context: "job_queue",
          action: "create_job",
          endpoint
        });
        onError?.(response.error);
        return reject(response.error);
      }

      jobId = response.data.jobId;

      if (!jobId) {
        throw new Error("No job ID received from server");
      }

      // Track this job globally (only if spinner should be shown)
      if (showSpinner) {
        jobTracker.add(jobId, endpoint);
      }

      // Subscribe to job updates
      socketManager.emit("subscribe", { event: "job", id: jobId });

      // Set timeout
      timeoutId = setTimeout(() => {
        cleanup();
        const timeoutError = new Error(`Job timeout after ${timeout}ms`);
        errorTracker.captureError(timeoutError, "error", {
          context: "job_queue",
          action: "job_timeout",
          endpoint,
          jobId,
          timeout
        });
        if (endpoint && !endpoint.includes("/suggest")) {
          alertStore.show("Job timed out", "error");
        }
        reject(timeoutError);
      }, timeout);

      // Create cleanup function to remove listener
      let cleanupListener: (() => void) | null = null;
      
      // Cleanup function
      const cleanup = () => {
        clearTimeout(timeoutId);
        if (jobId) {
          if (showSpinner) {
            jobTracker.remove(jobId);
          }
          socketManager.emit("unsubscribe", { event: "job", id: jobId });
        }
        if (cleanupListener) {
          cleanupListener();
        }
      };

      // Job handler
      const jobHandler = (job: any) => {
        if (job.id !== jobId || !jobId) return;

        // Update job status in tracker (only if spinner should be shown)
        if (job.status && showSpinner) {
          jobTracker.updateStatus(jobId, job.status);
        }

        if (job.status === "completed") {
          if (showSpinner) {
            jobTracker.setSuccess(true);
          }
          cleanup();

          // Success handling
          if (successMessage) {
            alertStore.show(successMessage);
          }

          onSuccess?.(job.result, jobId, job);
          resolve(job.result);
        } else if (job.status === "failed") {
          if (showSpinner) {
            jobTracker.setSuccess(false);
          }
          cleanup();

          // Error handling
          errorTracker.captureError(job.error as Error | string, "error", {
            context: "job_queue",
            action: "job_failed",
            endpoint,
            jobId,
            jobStatus: job.status
          });
          alertStore.show(errorMessage, "error");
          onError?.(job.error);
          reject(job.error);
        } else if (job.status === "running" && job.progress !== undefined) {
          // Progress update
          onProgress?.(job.progress);
        }
      };

      cleanupListener = socketManager.on("job", jobHandler, "jobQueue");
    } catch (error) {
      if (jobId && showSpinner) {
        jobTracker.remove(jobId);
      }
      errorTracker.captureError(error as Error | string, "error", {
        context: "job_queue",
        action: "job_exception",
        endpoint,
        jobId
      });
      alertStore.show(errorMessage, "error");
      onError?.(error);
      reject(error);
    }
  });
}
